import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../../../../../../components/Common/UI/Dialog';
import DataTable from '../../../../../../../../../components/Common/UI/DataTable';
import Separator from '../../../../../../../../../components/Common/UI/Separator';
import { Button } from '../../../../../../../../../components/Common/UI/Button';
import { notifySuccess } from '../../../../../../../../../components/Common/UI/Notify';
import Icons from '../../../../../../../../../components/Common/Icons/Icons';
import { getDivertedCol, DivertedSamplesRes } from './columns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../../../components/Common/UI/Select';
import { Textarea } from '../../../../../../../../../components/Common/UI/TextArea';
import { approveJob, getJobById, jobStatusData } from '../../../../../data/jobStatus';

export const CampaignDialogData = [
  { key: 'ID Number:', value: 39876 },
  { key: 'Start Date:', value: '1 March 2022' },
  { key: 'Application:', value: 'Monthly' },
  { key: 'Description:', value: 'test_009' },
  { key: 'End Date:', value: '1 March 2022' },
];

interface DivertedSamplesDialogProps {
  selectedSamples: DivertedSamplesRes[];
  onRemoveSample: (accountNumber: string) => void;
}

function DivertedSamplesDialog({ selectedSamples, onRemoveSample }: DivertedSamplesDialogProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasSelectedRecords = selectedSamples.length > 0;
  const job = getJobById(searchParams.get('job')) ?? jobStatusData[0];

  const handleOpenChange = (isOpen: boolean) => {
    setModalOpen(isOpen);
    if (!isOpen) {
      setConfirmOpen(false);
    }
  };

  const handleConfirmApprove = () => {
    if (job) {
      approveJob(job.id, selectedSamples);
    }
    setConfirmOpen(false);
    setModalOpen(false);
    notifySuccess('Job Approved');
    navigate('/infoTrac/job-status');
  };

  return (
    <Dialog open={modalOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger variant="filled" className="py-2 px-4 mt-2 mb-4 mr-4">
        Submit
      </DialogTrigger>
      <DialogContent className="overflow-y-auto max-h-[98%]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4 mb-[10px]">
            <Icons.USPSScan width={32} height={32} />
            <p className="text-xl leading-none">Diverted Samples</p>
          </DialogTitle>
        </DialogHeader>

        <DataTable columns={getDivertedCol(onRemoveSample)} data={selectedSamples} variant="bordered" />

        <div className="grid grid-cols-[170px_1fr] gap-4">
          <Select defaultValue="toBank">
            <SelectTrigger className="w-full" variant="white">
              <SelectValue placeholder="Send to Bank" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="toBank">Send to Bank</SelectItem>
              <SelectItem value="shredDocument">Shred Document</SelectItem>
            </SelectContent>
          </Select>

          <Textarea variant="transparent" placeholder="Comments:" />
        </div>

        <p className="text-destructive text-sm">
          IMPORTANT: You cannot make changes to this order after clicking &apos;Approve&lsquo;. By clicking
          &apos;Approve&lsquo;, you represent that you have reviewed this order and approve it in all respects including
          quantity and format.
        </p>

        <Separator className="!mt-4" />

        <div className="flex justify-end gap-4">
          <Button variant="default" disabled={!hasSelectedRecords} onClick={() => setConfirmOpen(true)}>
            Approve
          </Button>
          <Button
            variant="destructive"
            className="bg-destructive hover:bg-destructive hover:opacity-85"
            onClick={() => {
              setModalOpen(false);
              notifySuccess('Sample Disapproved');
            }}
          >
            Disapprove
          </Button>
        </div>
      </DialogContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-xl z-[60]">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">Approve Print Job</DialogTitle>
            <DialogDescription className="text-base text-foreground font-semibold">
              {job?.jobNum} ({job?.applicationId})
            </DialogDescription>
          </DialogHeader>

          <div>Approving this job will release the documents to be printed and mailed out.</div>

          <div className="text-destructive text-sm">
            <span className="font-bold">IMPORTANT:</span> You cannot make changes to this order after clicking
            &apos;Approve&apos;. By clicking &apos;Approve&apos;, you represent that you have reviewed this order and
            approve it in all respects including quantity and format.
          </div>

          <div className="flex justify-center gap-4 mt-4">
            <Button variant="default" onClick={handleConfirmApprove}>
              Approve
            </Button>
            <Button variant="dark" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default DivertedSamplesDialog;
